import { useId, useRef, type ReactNode, type RefObject } from "react";
import { SlidersHorizontal, X } from "lucide-react";
import { useDialogFocus } from "@/hooks/useDialogFocus";
import "./OperationalFilterSurface.css";

export type OperationalFilterSurfaceProps = {
  open: boolean;
  activeCount: number;
  title?: string;
  onOpenChange: (open: boolean) => void;
  onClear: () => void;
  onApply: () => void;
  onCancel?: () => void;
  applyDisabled?: boolean;
  children: ReactNode;
};

type OperationalFilterDialogProps = Omit<
  OperationalFilterSurfaceProps,
  "open" | "activeCount"
> & {
  panelId: string;
  title: string;
  titleId: string;
  triggerRef: RefObject<HTMLButtonElement | null>;
};

function OperationalFilterDialog({
  panelId,
  title,
  titleId,
  triggerRef,
  onOpenChange,
  onClear,
  onApply,
  onCancel,
  applyDisabled,
  children,
}: OperationalFilterDialogProps) {
  const dialogRef = useRef<HTMLElement>(null);

  function dismissAsCancelled() {
    onCancel?.();
    onOpenChange(false);
  }

  useDialogFocus(dialogRef, dismissAsCancelled, {
    restoreTarget: triggerRef.current,
  });

  return (
    <div className="operational-filter-layer">
      <button
        type="button"
        aria-label="Descartar alterações dos filtros"
        tabIndex={-1}
        className="operational-filter-backdrop"
        onClick={dismissAsCancelled}
      />
      <section
        ref={dialogRef}
        id={panelId}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="operational-filter-panel"
      >
        <header className="operational-filter-header">
          <h2 id={titleId}>{title}</h2>
          <button
            type="button"
            aria-label="Fechar filtros"
            className="operational-filter-close"
            onClick={dismissAsCancelled}
          >
            <X size={18} aria-hidden="true" />
          </button>
        </header>

        <div className="operational-filter-fields">{children}</div>

        <footer className="operational-filter-actions">
          <button
            type="button"
            className="operational-filter-action operational-filter-action--clear"
            onClick={onClear}
          >
            Limpar
          </button>
          <button
            type="button"
            className="operational-filter-action operational-filter-action--apply"
            disabled={applyDisabled}
            onClick={() => {
              if (applyDisabled) return;
              onApply();
              onOpenChange(false);
            }}
          >
            Aplicar
          </button>
        </footer>
      </section>
    </div>
  );
}

export function OperationalFilterSurface({
  open,
  activeCount,
  title = "Filtros",
  onOpenChange,
  onClear,
  onApply,
  onCancel,
  applyDisabled = false,
  children,
}: OperationalFilterSurfaceProps) {
  const panelId = useId();
  const titleId = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const safeActiveCount = Math.max(0, Math.floor(activeCount));
  const triggerLabel =
    safeActiveCount === 0
      ? title
      : `${title}, ${safeActiveCount} ${safeActiveCount === 1 ? "filtro ativo" : "filtros ativos"}`;

  function dismissAsCancelled() {
    onCancel?.();
    onOpenChange(false);
  }

  return (
    <div className="operational-filter-surface">
      <button
        ref={triggerRef}
        type="button"
        aria-label={triggerLabel}
        aria-expanded={open}
        aria-controls={panelId}
        data-active={safeActiveCount > 0 ? "true" : undefined}
        className="operational-filter-trigger"
        onClick={() => {
          if (open) {
            dismissAsCancelled();
          } else {
            onOpenChange(true);
          }
        }}
      >
        <SlidersHorizontal size={16} aria-hidden="true" />
        <span>{title}</span>
        {safeActiveCount > 0 && (
          <span className="operational-filter-count" aria-hidden="true">
            {safeActiveCount} {safeActiveCount === 1 ? "ativo" : "ativos"}
          </span>
        )}
      </button>

      {safeActiveCount > 0 && (
        <button
          type="button"
          aria-label="Limpar filtros"
          title="Limpar filtros"
          className="operational-filter-quick-clear"
          onClick={onClear}
        >
          <X size={14} aria-hidden="true" />
          <span>Limpar</span>
        </button>
      )}

      {open && (
        <OperationalFilterDialog
          panelId={panelId}
          title={title}
          titleId={titleId}
          triggerRef={triggerRef}
          onOpenChange={onOpenChange}
          onClear={onClear}
          onApply={onApply}
          onCancel={onCancel}
          applyDisabled={applyDisabled}
        >
          {children}
        </OperationalFilterDialog>
      )}
    </div>
  );
}
